/* Shared admin UI primitives */
import { Loader2 } from "lucide-react";

export const Input = ({ label, hint, error, testId, className = "", ...props }) => (
  <label className="block">
    {label && (
      <span className="mb-1.5 inline-block text-xs font-semibold uppercase tracking-[0.12em] text-slate-700">
        {label}
      </span>
    )}
    <input
      {...props}
      data-testid={testId}
      className={`block w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition-colors focus:border-[hsl(var(--aoa-royal))] ${className}`}
    />
    {hint && <span className="mt-1 block text-[11px] text-slate-500">{hint}</span>}
    {error && <span className="mt-1 block text-[11px] text-red-600">{error}</span>}
  </label>
);

export const Textarea = ({ label, testId, className = "", ...props }) => (
  <label className="block">
    {label && (
      <span className="mb-1.5 inline-block text-xs font-semibold uppercase tracking-[0.12em] text-slate-700">
        {label}
      </span>
    )}
    <textarea
      {...props}
      data-testid={testId}
      className={`block w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition-colors focus:border-[hsl(var(--aoa-royal))] resize-y ${className}`}
    />
  </label>
);

export const Button = ({ children, loading, variant = "primary", testId, ...props }) => {
  const base = "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed";
  const styles = {
    primary: "bg-[hsl(var(--aoa-royal))] text-white hover:bg-[hsl(var(--aoa-navy))]",
    outline: "border border-slate-300 bg-white text-[hsl(var(--aoa-navy))] hover:border-[hsl(var(--aoa-royal))] hover:text-[hsl(var(--aoa-royal))]",
    ghost: "text-slate-600 hover:text-[hsl(var(--aoa-navy))] hover:bg-slate-100",
    danger: "bg-red-600 text-white hover:bg-red-700",
  };
  return (
    <button {...props} data-testid={testId} disabled={loading || props.disabled} className={`${base} ${styles[variant]}`}>
      {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {children}
    </button>
  );
};

export const Card = ({ children, className = "", testId }) => (
  <div data-testid={testId} className={`rounded-xl border border-slate-200 bg-white ${className}`}>
    {children}
  </div>
);

export const PageHeader = ({ title, description, actions }) => (
  <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
    <div>
      <h1 className="font-display text-2xl md:text-3xl font-bold text-[hsl(var(--aoa-navy))]">{title}</h1>
      {description && <p className="mt-1.5 text-sm text-slate-600">{description}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Switch = ({ checked, onChange, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    data-testid={testId}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-[hsl(var(--aoa-royal))]" : "bg-slate-300"}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`}
    />
  </button>
);

export const Spinner = () => (
  <div className="flex items-center justify-center py-16 text-slate-400">
    <Loader2 className="h-5 w-5 animate-spin" />
  </div>
);
